import { Button, Empty } from 'antd';
import React, {
    useCallback, useEffect, useState,
} from 'react';
import { useMapEngineFallback } from './hooks/useMapEngineFallback';
import { useMapboxErrorDetection } from './hooks/useMapboxErrorDetection';
import MapLoader from './MapLoader';
import { MapEngine } from './types';

interface MapFallbackRenderArgs {
    reportLoaded: () => void;
    reportError: (reason: string) => void;
}

interface MapboxRenderArgs extends MapFallbackRenderArgs {
    mapboxErrorHandler: (event: { error?: { status?: number; message?: string }; sourceId?: string }) => void;
}

interface MapFallbackBoundaryProps {
    persistKey?: string;
    persistTtlMs?: number;
    fallbackTimeoutMs?: number;
    initialEngine?: MapEngine;
    renderMapbox: (args: MapboxRenderArgs) => React.ReactNode;
    renderGoogle: (args: MapFallbackRenderArgs) => React.ReactNode;
}

const fallbackContainerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
};

const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
};

const MapFallbackBoundary = ({
    persistKey,
    persistTtlMs,
    fallbackTimeoutMs,
    initialEngine,
    renderMapbox,
    renderGoogle,
}: MapFallbackBoundaryProps) => {
    const {
        engine, googleFailed, reportLoaded, reportError, retry,
    } = useMapEngineFallback({
        persistKey, persistTtlMs, fallbackTimeoutMs, initialEngine,
    });

    const { handler: mapboxErrorHandler } = useMapboxErrorDetection({ onFatalError: reportError });

    const [mapboxLoaded, setMapboxLoaded] = useState(false);

    useEffect(() => {
        if (engine === 'mapbox') setMapboxLoaded(false);
    }, [engine]);

    const handleReportLoaded = useCallback(() => {
        setMapboxLoaded(true);
        reportLoaded();
    }, [reportLoaded]);

    if (engine === 'google' && googleFailed) {
        return (
            <div style={fallbackContainerStyle}>
                <Empty description="Mapa no disponible" />
                <Button type="primary" onClick={retry}>Reintentar</Button>
            </div>
        );
    }

    if (engine === 'mapbox') {
        return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {renderMapbox({ reportLoaded: handleReportLoaded, reportError, mapboxErrorHandler })}
                {!mapboxLoaded && <MapLoader style={overlayStyle} />}
            </div>
        );
    }

    return <>{renderGoogle({ reportLoaded, reportError })}</>;
};

export default MapFallbackBoundary;
